import type { AxiosError } from 'axios';
import type { RouteParamsRaw } from 'vue-router';
import { isThresholdErrorMessage } from '@/util';

/** Backend error JSON shape we narrow from axios `response.data`. */
export interface ApiErrorResponseBody {
  message?: string | string[];
}

export interface ErrorScreenProps extends RouteParamsRaw {
  icon: string;
  title: string;
  message: string;
  backEnabled?: string; // See it as a boolean. That's a type limitation, 'cause it has to be number or string.
  showErrorLogs?: string;
  errorData?: string; // JSON.stringify(error.response?.data), set only with showErrorLogs
}

export default function useApiErrors() {
  const makeErrorScreenParams = (error: AxiosError): ErrorScreenProps => {
    if (error.response?.data) {
      const data = error.response.data as ApiErrorResponseBody;
      let message = data.message;
      message = Array.isArray(message) ? message[0] : message;

      if (typeof message === 'string') {
        if (
          /^You must be within a [A-Za-z0-9. ]+ radius to check in to your appointment$/gu.test(
            message
          )
        ) {
          return {
            icon: 'mdi-map-marker-off-outline',
            title: 'We could not validate your location',
            backEnabled: '1',
            message
          };
        }

        if (isThresholdErrorMessage(message)) {
          return {
            icon: 'mdi-clock-alert-outline',
            title: 'Not on time to check-in',
            backEnabled: '1',
            message
          };
        }

        if (/^(Warehouse|Org) is not configured for checking in$/gu.test(message)) {
          return {
            icon: 'mdi-alert-circle',
            title: 'Warehouse not available',
            backEnabled: '0',
            message: 'Please contact the facility personnel to proceed with manual check-in'
          };
        }

        if (/^You can't check in to an appointment marked as [A-Za-z]+$/gu.test(message)) {
          return {
            icon: 'mdi-calendar-remove',
            title: message,
            backEnabled: '1',
            message: ''
          };
        }

        if (
          /^You can't check in to an appointment already associated with an asset visit$/gu.test(
            message
          )
        ) {
          return {
            icon: 'mdi-alert-circle',
            title: message,
            backEnabled: '1',
            message: ''
          };
        }

        if (/^Phone .* is already assigned to an ongoing check-in$/gu.test(message)) {
          return {
            icon: 'mdi-cellphone-off',
            title: message,
            backEnabled: '1',
            showErrorInline: '1',
            message: ''
          };
        }

        if (/must be shorter than or equal to \d+ characters$/gu.test(message)) {
          return {
            icon: 'mdi-text-box-remove-outline',
            title: 'Input exceeds character limit',
            backEnabled: '1',
            showErrorInline: '1',
            message
          };
        }
      }
    }

    return {
      icon: 'mdi-alert-circle-outline',
      title: 'Check-in failed. Please, try again.',
      backEnabled: '1',
      showErrorLogs: '1',
      errorData: error.response?.data ? JSON.stringify(error.response.data) : null,
      message: ''
    };
  };

  return {
    makeErrorScreenParams
  };
}
